import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, GripVertical, TimerOff, Timer } from 'lucide-react';

/**
 * DashboardLayout - Shared page wrapper for all dashboard pages
 *
 * Props:
 *   id        - Unique dashboard id (used for localStorage key)
 *   title     - Page title
 *   icon      - Lucide icon component
 *   onRefresh - Async function to call for data loading
 *   loading   - Whether data is currently loading
 *   error     - Error message string (null if no error)
 *   children  - Dashboard grid content
 */
const DashboardLayout = ({ id, title, icon: Icon, onRefresh, loading, error, children }) => {
  const storageKey = `pmd-dashboard-refresh-${id}`;
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved !== null ? saved === 'true' : true;
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const hasLoadedOnce = useRef(false);
  const refreshIntervalRef = useRef(null);
  const menuRef = useRef(null);

  // Track when loading completes to set lastUpdated
  const prevLoading = useRef(loading);
  useEffect(() => {
    if (prevLoading.current && !loading) {
      hasLoadedOnce.current = true;
      setLastUpdated(new Date());
    }
    prevLoading.current = loading;
  }, [loading]);

  // Set up auto-refresh interval
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    if (autoRefresh && onRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        onRefresh();
      }, 30000);
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, onRefresh]);

  // Initial load
  useEffect(() => {
    if (onRefresh) onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist auto-refresh preference
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefresh(prev => {
      const next = !prev;
      localStorage.setItem(storageKey, String(next));
      return next;
    });
    setMenuOpen(false);
  }, [storageKey]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show spinner only on first load
  const showLoading = loading && !hasLoadedOnce.current;

  if (showLoading) {
    return (
      <div className="dev-module">
        <div className="dev-module-header">
          <div className="dev-module-header-left">
            <h1>
              {Icon && <Icon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
              {title}
            </h1>
          </div>
        </div>
        <div className="dashboard-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dev-module">
        <div className="dev-module-header">
          <div className="dev-module-header-left">
            <h1>
              {Icon && <Icon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
              {title}
            </h1>
          </div>
        </div>
        <div className="dashboard-error">
          <p>{error}</p>
          <button className="crud-btn" onClick={onRefresh}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dev-module">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            {Icon && <Icon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
            {title}
          </h1>
          {lastUpdated && (
            <span className="dev-module-meta">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="dev-module-header-right">
          <button
            className="crud-btn"
            onClick={onRefresh}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
          <div className="action-menu" ref={menuRef}>
            <button
              className="action-menu-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              title="Options"
            >
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={toggleAutoRefresh}>
                  {autoRefresh ? <TimerOff size={16} /> : <Timer size={16} />}
                  <span>{autoRefresh ? 'Disable Auto-Refresh' : 'Enable Auto-Refresh'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {children}
      </div>
    </div>
  );
};

export default DashboardLayout;
